import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { FeedbackIssueType } from '../models/chat.models';

export interface DislikeFeedback {
  issueTypeId: number | null;
  details: string;
}

@Component({
  selector: 'deo-ai-assist-feedback',
  standalone: false,
  templateUrl: './deo-ai-assist-feedback.component.html',
  styleUrls: ['./deo-ai-assist-feedback.component.scss']
})
export class DeoAiAssistFeedbackComponent {
  @ViewChild('likeFeedbackModalTpl', { static: true }) likeFeedbackModalTpl!: TemplateRef<void>;
  @ViewChild('dislikeFeedbackModalTpl', { static: true }) dislikeFeedbackModalTpl!: TemplateRef<void>;

  @Input() isTyping = false;
  @Input() isAssistant = false;
  @Input() isPositiveFeedback: boolean | null | undefined;
  @Input() isBusy = false;

  @Output() like = new EventEmitter<void>();
  @Output() dislike = new EventEmitter<void>();
  @Output() copyClicked = new EventEmitter<void>();
  @Output() likeFeedbackSubmit = new EventEmitter<string>();
  @Output() dislikeFeedbackSubmit = new EventEmitter<DislikeFeedback>();
  @Output() feedbackCleared$ = new EventEmitter<void>();

  private likeFeedbackModalRef?: NzModalRef;
  private dislikeFeedbackModalRef?: NzModalRef;
  likeFeedbackControl = new FormControl('');
  dislikeFeedbackTypeControl = new FormControl<FeedbackIssueType | null>(null);
  dislikeFeedbackDetailsControl = new FormControl('');

  feedbackTypes: FeedbackIssueType[] = [
    { id: 1, label: 'UI bug', title: 'UI bug' },
    { id: 2, label: 'Overactive refusal', title: 'Overactive refusal' },
    { id: 3, label: 'Did not fully follow my request', title: 'Did not fully follow my request' },
    { id: 4, label: 'Not factually correct', title: 'Not factually correct' },
    { id: 5, label: 'Incomplete response', title: 'Incomplete response' },
    { id: 6, label: 'Other', title: 'Other' }
  ];

  constructor(private readonly _modalService: NzModalService) {}

  onLike(): void {
    this.like.emit();
  }

  onLikeClick(): void {
    if (this.isPositiveFeedback === true) {
      this.feedbackCleared$.emit();
      return;
    }

    if (this.likeFeedbackModalRef) {
      return;
    }

    this.likeFeedbackControl.setValue('');

    this.likeFeedbackModalRef = this._modalService.create({
      nzTitle: '',
      nzContent: this.likeFeedbackModalTpl,
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: true,
      nzCentered: true,
      nzWidth: 560
    });

    this.likeFeedbackModalRef.afterClose.subscribe(() => {
      this.likeFeedbackModalRef = undefined;
    });
  }

  onLikeFeedbackCancel(): void {
    this.likeFeedbackModalRef?.destroy();
    this.likeFeedbackModalRef = undefined;
  }

  onLikeFeedbackSubmit(): void {
    const feedbackDetails = (this.likeFeedbackControl.value || '').toString().trim();
    this.onLike();
    this.likeFeedbackSubmit.emit(feedbackDetails);
    this.likeFeedbackModalRef?.destroy();
    this.likeFeedbackModalRef = undefined;
  }

  onDislikeClick(): void {
    if (this.isPositiveFeedback === false) {
      this.feedbackCleared$.emit();
      return;
    }

    if (this.dislikeFeedbackModalRef) {
      return;
    }

    const defaultFeedbackType = this.feedbackTypes.find(type => type.id === 3) ?? this.feedbackTypes[0] ?? null;
    this.dislikeFeedbackTypeControl.setValue(defaultFeedbackType);
    this.dislikeFeedbackDetailsControl.setValue('');

    this.dislikeFeedbackModalRef = this._modalService.create({
      nzTitle: '',
      nzContent: this.dislikeFeedbackModalTpl,
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: true,
      nzCentered: true,
      nzWidth: 560
    });

    this.dislikeFeedbackModalRef.afterClose.subscribe(() => {
      this.dislikeFeedbackModalRef = undefined;
    });
  }

  onDislikeFeedbackCancel(): void {
    this.dislikeFeedbackModalRef?.destroy();
    this.dislikeFeedbackModalRef = undefined;
  }

  onDislikeFeedbackSubmit(): void {
    const issueTypeId = this.dislikeFeedbackTypeControl.value?.id ?? null;
    const details = (this.dislikeFeedbackDetailsControl.value || '').toString().trim();

    this.onDislike();

    this.dislikeFeedbackSubmit.emit({
      issueTypeId,
      details
    });

    this.dislikeFeedbackModalRef?.destroy();
    this.dislikeFeedbackModalRef = undefined;
  }

  onDislike(): void {
    this.dislike.emit();
  }

  onCopy(): void {
    this.copyClicked.emit();
  }
}
